<template>
  <div class="mobile-enquiry-page">
    <MobileLayout :showTopBar="false">
      <div class="mq-wrap" :class="{ 'fade-in': animateIn }">
        <!-- 固定标题 -->
        <div class="mq-header"  :style="items.length > 0 ? { 'border-bottom': '1rem solid #000' } : {}">
          <img :src="brandLogo" alt="denHolm" class="mq-logo" @click="goHome" />
          <div class="mq-title">Enquiry List ({{ items.length }})</div>
        </div>

        <!-- 列表滚动区 -->
        <div class="mq-scroll-area" ref="scrollArea">
          <!-- ★ loading 状态：显示骨架屏，但很快消失 -->
          <div v-if="loading && items.length>0" class="mq-loading">
            <div class="mq-skeleton" v-for="i in 3" :key="i">
              <div class="skeleton-thumb"></div>
              <div class="skeleton-body">
                <div class="skeleton-line"></div>
                <div class="skeleton-line short"></div>
                <div class="skeleton-line shorter"></div>
              </div>
            </div>
          </div>

          <!-- 列表 -->
          <transition-group v-else name="list" tag="div" class="mq-list">
            <div
              class="mq-item"
              v-for="(item, index) in items"
              :key="item.CartID"
              :class="{ expanded: item.isCustomise }"
             
            >
              <div class="mq-thumb">
                <!-- ★ 显示图片：如果真实图片已加载，显示真实图片；否则显示占位图 -->
                <img
                  :src="item.displayImage"
                  alt=""
                  class="lazy-image"
                  :class="{ loaded: item.imageLoaded }"
                />
              </div>
              <div class="mq-body">
                <div class="mq-top">
                  <div class="mq-meta">
                    <div class="mq-cat">{{ item.category }}</div>
                    <div class="mq-name">{{ item.name }}</div>
                  </div>
                  <button class="mq-remove" type="button" @click="removeEnquiryListItem(item.CartID)">
                    Remove
                  </button>
                </div>
                <div class="mq-bottom">
                  <label class="mq-customise">
                    <input type="checkbox" v-model="item.isCustomise" @change="updateCustomizeStatus(item)" />
                    <img :src="item.isCustomise ? ckedIcon : ckIcon" class="mq-check-icon" alt="checkbox" />
                    Customise
                  </label>
                </div>
                <transition name="expand">
                  <div v-if="item.isCustomise" class="mq-customise-input">
                    <div class="mq-input-wrapper">
                      <textarea
                        v-model="item.customizationText"
                        class="mq-customise-textarea"
                        rows="1"
                        @focus="handleInputFocus(item)"
                        @blur="handleInputBlur(item)"
                        @input="resizeTextarea"
                      ></textarea>
                      <label
                        class="mq-floating-label"
                        :class="{
                          'has-text': item.customizationText && item.customizationText.length > 0,
                          focused: item.inputFocused
                        }"
                      >
                        Tell us how you'd make it yours.
                      </label>
                    </div>
                    <div v-show="item.showSaveBtn" class="mq-save-btn" @mousedown.prevent="saveCustomization(item)">
                      <span class="mq-save-text">[Save]</span>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </transition-group>

          <!-- 空状态 -->
          <div v-if="!loading && items.length === 0" class="mq-empty">
            <div class="mq-empty-text">
              <p>Bit empty in here, isn't it?</p>
              <p>Go find something nice.</p>
            </div>
            <div class="mq-empty-actions">
              <button class="mq-empty-btn back" type="button" @click="$router.back()">Back</button>
              <button class="mq-empty-btn browse" type="button" @click="$router.push('/m/product')">Browse</button>
            </div>
          </div>

          <!-- 底部占位 -->
          <div class="mq-bottom-placeholder"></div>
        </div>

        <!-- 固定底部按钮 -->
        <div v-if="!loading && items.length > 0" class="mq-next">
		  <button class="mq-next-btn" type="button" @click="$router.back()" style="border:none;margin-right: 10rem;">Back</button>
          <button class="mq-next-btn" type="button" @click="goNext" style="margin-left: 10rem;">Next</button>
        </div>
      </div>
    </MobileLayout>
  </div>
</template>

<script>
import MobileLayout from '@/components/mobile/MobileLayout.vue'
import { showMessage } from '@/config/message'
import { getUser } from '@/config/auth'
import {
  getCartItems,
  removeFromCart,
  updateCartItem,
  addToCart,
} from '@/api/Enquiry'
import {
  getLocalCart,
  removeFromLocalCart,
  updateLocalCartCustomization,
  clearLocalCart,
} from '@/utils/localCart'

const fallbackImage = require('@/assets/mobile/product/Rectangle 37.png')
const brandLogo = require('@/assets/logo_b.png')
const ckIcon = require('@/assets/mobile/icon/ck.png')
const ckedIcon = require('@/assets/mobile/icon/cked.png')

// ★ 占位图（1x1 像素透明 GIF，最小体积）
const PLACEHOLDER_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

export default {
  name: 'MobileEnquiry',
  components: { MobileLayout },
  data() {
    return {
      items: [],
      loading: true,
      loggedInUser: null,
      brandLogo,
      ckIcon,
      ckedIcon,
      animateIn: false,
    }
  },
  computed: {
    isLoggedIn() {
      return !!this.loggedInUser
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.animateIn = true
    })
    this.loadUser()
    this.loadCartItems()
    window.addEventListener('mobile-login-success', this.onLoginSuccess)
  },
  beforeDestroy() {
    window.removeEventListener('mobile-login-success', this.onLoginSuccess)
  },
  watch: {
    $route: {
      handler(to) {
        if (to.path === '/m/enquiry') {
          this.loadUser()
          this.loadCartItems()
          this.animateIn = false
          this.$nextTick(() => {
            this.animateIn = true
          })
        }
      },
      immediate: true,
    },
  },
  methods: {
    loadUser() {
      this.loggedInUser = getUser()
    },
    goHome() {
      this.$router.push('/m')
    },

    /**
     * ★ 核心方法：先显示列表（占位图），再异步加载真实图片
     */
    async loadCartItems() {
      this.loading = true

      if (this.isLoggedIn) {
        try {
          const items = await getCartItems(this.loggedInUser.UserID)

          // ★ 第一步：立即显示列表，使用占位图
          this.items = items.map((item) => ({
            CartID: item.CartID,
            ProductID: item.ProductID,
            // 先显示占位图
            displayImage: PLACEHOLDER_IMAGE,
            // 真实图片 URL
            realImage: item.ImageURL || fallbackImage,
            category: item.CategoryName,
            name: item.ProductName,
            isCustomise: item.IsCustomized || false,
            customizationText: item.CustomisationText || '',
            showSaveBtn: false,
            inputFocused: false,
            imageLoaded: false, // 标记图片是否已加载
          }))

          // ★ 第二步：立即结束 loading，让用户看到列表
          this.loading = false
		  
		  // ★ 数据已就绪，通知布局显示
		        this.$nextTick(() => {
		          window.dispatchEvent(new Event('page-ready'))
		        })

          // ★ 第三步：异步加载真实图片
          this.preloadImages()

        } catch (error) {
          console.error('Failed to load cart items:', error)
          showMessage('Failed to load enquiry list', 'error')
          this.loading = false
        }
      } else {
        // 本地购物车
        const localItems = getLocalCart()
        this.items = localItems.map((item) => ({
          CartID: item.localId,
          ProductID: item.ProductID,
          displayImage: PLACEHOLDER_IMAGE,
          realImage: item.image || fallbackImage,
          category: item.category,
          name: item.name,
          isCustomise: !!item.customizationText,
          customizationText: item.customizationText || '',
          showSaveBtn: false,
          inputFocused: false,
          imageLoaded: false,
        }))
        this.loading = false
        // 异步加载本地图片
        this.preloadImages()
      }
    },

    /**
     * ★ 异步预加载真实图片，加载完成后替换占位图
     */
    preloadImages() {
      this.items.forEach((item, index) => {
        if (!item.realImage) return
        // 如果已经加载过了，跳过
        if (item.imageLoaded) return

        const img = new Image()
        img.onload = () => {
          // 图片加载成功，替换为真实图片
          this.$set(this.items[index], 'displayImage', item.realImage)
          this.$set(this.items[index], 'imageLoaded', true)
        }
        img.onerror = () => {
          // 加载失败，使用备用图片
          console.warn('Image load failed:', item.realImage)
          this.$set(this.items[index], 'displayImage', fallbackImage)
          this.$set(this.items[index], 'imageLoaded', true)
        }
        // 开始加载
        img.src = item.realImage
      })
    },

    async removeEnquiryListItem(cartId) {
      if (this.isLoggedIn) {
        try {
          await removeFromCart(cartId, this.loggedInUser.UserID)
          this.items = this.items.filter((item) => item.CartID !== cartId)
        } catch (error) {
          console.error('Remove failed:', error)
          showMessage('Failed to remove item', 'error')
        }
      } else {
        removeFromLocalCart(cartId)
        this.items = this.items.filter((item) => item.CartID !== cartId)
      }
    },

    async updateCustomizeStatus(item) {
      if (!item.isCustomise) {
        item.customizationText = ''
      }
      await this.saveCustomization(item)
    },

    async saveCustomization(item) {
      if (this.isLoggedIn) {
        try {
          const response = await updateCartItem({
            CartID: item.CartID,
            UserID: this.loggedInUser.UserID,
            CustomisationText: item.customizationText || '',
          })
          if (response && response.retCode === 1) {
            item.showSaveBtn = false
          } else {
            showMessage(response.retMsg || 'Save failed', 'error')
          }
        } catch (error) {
          console.error('Save failed:', error)
        }
      } else {
        updateLocalCartCustomization(item.CartID, item.customizationText)
        item.showSaveBtn = false
      }
    },

    handleInputFocus(item) {
      item.showSaveBtn = true
      item.inputFocused = true
    },

    handleInputBlur(item) {
      setTimeout(() => {
        item.showSaveBtn = false
        item.inputFocused = false
      }, 200)
    },

    resizeTextarea(event) {
      const textarea = event.target
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    },

    async syncLocalCartToServer() {
      const localItems = getLocalCart()
      if (localItems.length === 0 || !this.loggedInUser) return
      let successCount = 0
      for (const localItem of localItems) {
        try {
          const model = {
            ProductID: localItem.ProductID,
            UserID: this.loggedInUser.UserID,
            CustomisationText: localItem.customizationText || '',
          }
          const response = await addToCart(model)
          if (response.retCode === 1) successCount++
        } catch (err) {
          console.error('Sync item failed', localItem.ProductID, err)
        }
      }
      if (successCount > 0) {
        showMessage(`Synced ${successCount} item(s) to server`, 'success')
      }
      clearLocalCart()
      await this.loadCartItems()
    },

    async onLoginSuccess() {
      this.loadUser()
      await this.syncLocalCartToServer()
    },

    goNext() {
      this.$router.push('/m/enquiry-sub')
    },
  },
}
</script>

<style scoped>
/* ===== 根容器 ===== */
.mobile-enquiry-page {
  /* 无样式 */
}

/* ===== 主容器 ===== */
.mq-wrap {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 45rem;
  background: #fff;
  color: #000;
  font-family: 'Apercu-Medium', sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}
.mq-wrap.fade-in {
  opacity: 1;
}

/* ===== 标题区 ===== */
.mq-header {
  flex-shrink: 0;
  padding-bottom: 43rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: #fff;
  position: relative;
  z-index: 2;
}
.mq-logo {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  z-index: 10;
}
.mq-title {
  font-size: 18rem;
  font-weight: 600;
  margin-top: 130rem;
  background: #fff;
  width: 100%;
  padding-bottom: 10rem;
  z-index: 3;
  position: relative;
}

/* ===== 滚动区域 ===== */
.mq-scroll-area {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0;
}

/* ===== 骨架屏 ===== */
.mq-loading {
  padding: 0;
}

.mq-skeleton {
  display: grid;
  grid-template-columns: 147rem 1fr;
  min-height: 147rem;
  border-bottom: 1rem solid #000;
  background: #fff;
}

.skeleton-thumb {
  width: 147rem;
  height: 147rem;
  background: #e8e8e8;
  border-right: 1rem solid #000;
  animation: shimmer 1.2s ease-in-out infinite;
}

.skeleton-body {
  padding: 24rem 26rem;
  display: flex;
  flex-direction: column;
  gap: 10rem;
  justify-content: center;
}

.skeleton-line {
  height: 12rem;
  background: #e8e8e8;
  border-radius: 4rem;
  width: 75%;
  animation: shimmer 1.2s ease-in-out infinite;
}

.skeleton-line.short {
  width: 45%;
}

.skeleton-line.shorter {
  width: 30%;
}

@keyframes shimmer {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
}

/* ===== 列表 ===== */
.mq-list {
  display: flex;
  flex-direction: column;
 
}

/* ===== 列表项 ===== */
.mq-item {
  display: grid;
  grid-template-columns: 147rem 1fr;
  min-height: 147rem;
  max-height: 147rem;
  height: auto;
  transition: max-height 0.35s ease;
  position: relative;
  border-bottom: 1rem solid #000;
  overflow: hidden;
}
.mq-item.expanded {
  max-height: 999px;
}

/* ===== 缩略图 ===== */
.mq-thumb {
  width: 147rem;
  min-height: 147rem;
  height: 100%;
  overflow: hidden;
  align-self: stretch;
  background: #f5f5f5; /* ★ 加载完成前显示灰色背景 */
  border-right: 1rem solid #000;
}

.lazy-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: 0.3;
  transition: opacity 0.5s ease;
}

/* ★ 图片加载完成后淡入 */
.lazy-image.loaded {
  opacity: 1;
}

/* ===== 右侧内容 ===== */
.mq-body {
  padding: 24rem 26rem 24rem 26rem;
  display: flex;
  flex-direction: column;
  gap: 7rem;
  overflow: visible;
}
.mq-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30rem;
}
.mq-meta {
  display: flex;
  flex-direction: column;
}
.mq-cat {
  font-size: 9rem;
  color: #9b9b9b;
}
.mq-name {
  font-size: 13rem;
  font-weight: 500;
}
.mq-remove {
  background: none;
  border: none;
  font-size: 9rem;
  text-decoration: underline;
  padding: 0;
  color: #000;
}
.mq-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.mq-customise {
  display: inline-flex;
  align-items: center;
  gap: 8rem;
  font-size: 9rem;
  font-weight: 600;
  color: #000;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.mq-customise input {
  display: none;
}
.mq-check-icon {
  width: 13rem;
  height: 13rem;
  display: inline-block;
  flex-shrink: 0;
}

/* ===== 定制展开/收起 ===== */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.4s ease;
  max-height: 150px;
  opacity: 1;
  overflow: hidden;
}
.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  margin-top: 0;
}
.mq-customise-input {
  display: flex;
  flex-direction: column;
  gap: 8rem;
  margin-top: 4rem;
}
.mq-input-wrapper {
  position: relative;
  width: 100%;
}
.mq-customise-textarea {
  width: 100%;
  border: none;
  border-bottom: 1rem solid #000;
  padding: 8rem 0 1rem 0;
  font-size: 8rem;
  font-family: 'Apercu-Medium', sans-serif;
  resize: none;
  color: #000;
  background: transparent;
  outline: none;
  min-height: 24rem;
}
.mq-floating-label {
  position: absolute;
  left: 0;
  bottom: 5rem;
  font-size: 8rem;
  color: #999;
  pointer-events: none;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.mq-floating-label.has-text,
.mq-floating-label.focused {
  display: none;
}
.mq-save-btn {
  align-self: flex-end;
}
.mq-save-text {
  font-size: 10rem;
  font-weight: 600;
  text-decoration: underline;
  color: #000;
  cursor: pointer;
}

/* ===== 空状态 ===== */
.mq-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 20rem;
}
.mq-empty-text {
  font-size: 18rem;
  font-weight: 600;
  text-align: center;
  max-width: 247rem;
  margin-top: 30rem;
}
.mq-empty-actions {
  display: flex;
  justify-content: center;
  gap: 8rem;
  margin-top: 80rem;
}
.mq-empty-btn {
  height: 32rem;
  border-radius: 16rem;
  font-size: 15rem;
  font-weight: 500;
  font-family: 'Apercu-Medium', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.mq-empty-btn.back {
  width: 88rem;
  background: #fff;
  border: 1rem solid #000;
  color: #000;
}
.mq-empty-btn.browse {
  width: 98rem;
  background: #000;
  border: none;
  color: #fff;
}

/* ===== 删除动画 ===== */
.list-leave-active {
  transition: transform 0.5s ease, opacity 0.5s ease !important;
  position: absolute;
  left: 0;
  right: 0;
  z-index: 10;
  background: transparent !important;
  pointer-events: none;
  border-bottom: 1rem solid #000 !important;
}
.list-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.list-move {
  transition: transform 0.5s ease !important;
}

/* ===== 固定底部 Next 按钮 ===== */
.mq-next {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 45rem;
  height: 138rem;
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 20;
}
.mq-next::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 138rem;
  background: white;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  mask-image: linear-gradient(to bottom, transparent 0%, white 50%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, white 50%);
  pointer-events: none;
  z-index: 0;
}
.mq-next-btn {
  position: relative;
  z-index: 1;
  background: none;
  border: 1rem solid #000;
  color: #000;
  font-size: 13rem;
  font-weight: 500;
  font-family: 'Apercu-Medium', sans-serif;
  height: 28rem;
  width: 68rem;
  border-radius: 16rem;
  margin-top: 79rem;
  pointer-events: auto;
}

/* ===== 底部占位 ===== */
.mq-bottom-placeholder {
  height: 140rem;
}

/* ===== 响应式 ===== */
@media (max-width: 375px) {
  .mq-item {
    grid-template-columns: 110rem 1fr;
    min-height: 110rem;
  }
  .mq-thumb {
    width: 110rem;
    min-height: 110rem;
  }
  .mq-skeleton {
    grid-template-columns: 110rem 1fr;
    min-height: 110rem;
  }
  .skeleton-thumb {
    width: 110rem;
    height: 110rem;
  }
  .mq-body {
    padding: 16rem 18rem;
  }
  .mq-top {
    margin-bottom: 16rem;
  }
}
</style>